import React, { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { LariBaseDTO, LariError } from '../data/types/lari';
import { Mutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useRequiredContext from '../hooks/useRequiredContext';

export type LariMessagingContext = {
  showServerErrorModal: boolean;
  setShowServerErrorModal: (value: boolean) => void;
  lariDTO: LariBaseDTO;

  mutation: Mutation<unknown, unknown, LariBaseDTO>;
  setMutation: (value: Mutation<unknown, unknown, LariBaseDTO>) => void;

  errors: LariError[];
  addError: (error: LariError) => void;

  forbidden: boolean;
  setForbidden: React.Dispatch<React.SetStateAction<boolean>>;

  notFound: boolean;
  setNotFound: React.Dispatch<React.SetStateAction<boolean>>;

  concurrency: boolean;
  setConcurrency: React.Dispatch<React.SetStateAction<boolean>>;
  unhandledError: AxiosError;
  setUnhandledError: (value: AxiosError) => void;
  clearMessageContext: () => void;

  setOnContinue: ((onContinue: () => void) => void) | undefined;
  onContinue: () => void;

  onConfirm: () => void;
  setOnConfirm: (value: () => void) => void;
  confirmationMainText: string;
  setConfirmationMainText: (value: string) => void;
  confirmationSubText: string;
  setConfirmationSubText: (value: string) => void;
  openConfirmationModal: (onConfirm: () => void, mainText?: string, subText?: string) => void;
};

const LariMessagingContext = createContext<LariMessagingContext | undefined>(undefined);

const LariMessagingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [showServerErrorModal, setShowServerErrorModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [lariDTO, setLariDTO] = useState<LariBaseDTO | undefined>(undefined);
  const [mutation, setMutation] = useState<Mutation<unknown, unknown, LariBaseDTO> | null>();
  const [forbidden, setForbidden] = useState(false);
  const [unhandledError, setUnhandledError] = useState<AxiosError>();
  const [concurrency, setConcurrency] = useState(false);
  const [onContinue, setOnContinueInternal] = useState<(() => void) | undefined>(undefined);

  const [confirmationMainText, setConfirmationMainText] = useState<string | undefined>(undefined);
  const [confirmationSubText, setConfirmationSubText] = useState<string | undefined>(undefined);
  const [onConfirm, setOnConfirm] = useState<(() => void) | undefined>(undefined);
  const [errors, setErrors] = useState<LariError[]>([]);
  const [notFound, setNotFound] = useState(false);

  const addError = useCallback((error: LariError) => {
    setErrors((prev) => [...prev, error]);
  }, []);

  const openConfirmationModal = useCallback((onConfirm: () => void, mainText?: string, subText?: string) => {
    setConfirmationMainText(mainText);
    setConfirmationSubText(subText);
    setOnConfirm(() => onConfirm);
    setShowConfirmationModal(true);
  }, []);

  const setOnContinue = useCallback(
    (onContinue: (() => void) | undefined) => {
      setOnContinueInternal(() => {
        return onContinue;
      });
    },
    [setOnContinueInternal],
  );

  const clearMessageContext = useCallback(() => {
    setMutation(null);
    setLariDTO(undefined);
    setForbidden(false);
    setConcurrency(false);
    setShowConfirmationModal(false);
    setOnContinue(undefined);
    setShowServerErrorModal(false);
    setNotFound(false);
  }, [setOnContinue]);

  const value = useMemo(
    () =>
      ({
        showServerErrorModal,
        setShowServerErrorModal,
        lariDTO,
        setLariDTO,
        mutation,
        setMutation,
        unhandledError,
        setUnhandledError,
        forbidden,
        setForbidden,
        concurrency,
        setConcurrency,
        clearMessageContext,
        onContinue,
        setOnContinue,
        showConfirmationModal,
        setShowConfirmationModal,
        setConfirmationMainText,
        confirmationMainText,
        setConfirmationSubText,
        confirmationSubText,
        onConfirm,
        setOnConfirm,
        openConfirmationModal,
        errors,
        addError,
        notFound,
        setNotFound,
      }) as LariMessagingContext | undefined,
    [
      showServerErrorModal,
      setShowServerErrorModal,
      lariDTO,
      setLariDTO,
      mutation,
      setMutation,
      unhandledError,
      setUnhandledError,
      forbidden,
      setForbidden,
      concurrency,
      setConcurrency,
      clearMessageContext,
      onContinue,
      setOnContinue,
      showConfirmationModal,
      setShowConfirmationModal,
      setConfirmationMainText,
      confirmationMainText,
      setConfirmationSubText,
      confirmationSubText,
      onConfirm,
      setOnConfirm,
      openConfirmationModal,
      errors,
      addError,
      notFound,
      setNotFound,
    ],
  );

  return <LariMessagingContext.Provider value={value}>{children}</LariMessagingContext.Provider>;
};

const useLariMessaging = (): LariMessagingContext => useRequiredContext(LariMessagingContext);

export { LariMessagingProvider, useLariMessaging };
